<template>
  <div class="root">
    <div class="handle" @click="$emit('hide')">
      <a-icon type="right"/>
    </div>
    <div class="content">
      <div class="header">{{$t('calling.CollaborativeMember')}}</div>
      <div class="members">
        <div
          v-for="member in sortedMembers"
          :key="member.id"
          class="member"
          :class="{'hovered': hoveredMemberId === member.id || moreMenuId === member.id}"
          @pointerenter="hoverMember(member)"
          @pointerleave="hoverMember({})"
        >
          <div class="avatar-wrapper">
            <UserAvatar :user-avatar="allAvatar[`uid|${member.id}`]" class="user-avatar"/>
            <template v-if="member.inRoom">
              <svg-icon v-if="member.isAnnotator" icon-class="talking-mark-role"/>
              <svg-icon v-else-if="member.isGlassDevice" icon-class="talking-device-glass"/>
            </template>
            <div v-if="!member.inRoom" class="mask">
              <div v-if="member.isWaiting" class="loading-spinner">
                <div class="bounce1"></div>
                <div class="bounce2"></div>
                <div class="bounce3"></div>
              </div>
              <span v-else>{{ statusNameMap[member.status] }}</span>
            </div>
          </div>
          <div class="texts">
            <div class="name-role">
              <span class="name">{{ member.name }}</span>
              <span class="role" v-if="member.isHost">{{$t('home.Host')}}</span>
              <span class="role" v-else-if="member.isAnnotator">{{$t('calling.Annotator')}}</span>
            </div>
            <div class="corp-name">{{ member.corpName }}</div>
          </div>
          <div class="media-status">
            <span>
              <svg-icon v-if="member.cameraOn" icon-class="talking-member-camera"/>
            </span>
            <span>
              <svg-icon :icon-class="member.microphoneOn ? 'talking-member-microphone-on' : 'talking-member-microphone-off'"/>
            </span>
          </div>
          <div v-if="isShowNotInRoomOperation(member)" class="not-in-room-operation">
            <button class="call" @click="callMember(member)">{{$t('calling.Call')}}</button>
            <button class="kick" @click="kickOutMember(member)">{{$t('calling.Remove')}}</button>
          </div>
          <div v-if="isShowInRoomOperation(member)" class="in-room-operation">
            <button class="mark" @click="toggleMarkRole(member)">
              {{ member.isAnnotator ? $t('calling.CancelAnnotationPermission') : $t('calling.GrantingAnnotationPermission') }}
            </button>
            <div class="more" :class="{opened: member.id === moreMenuId}" @click.self.capture.stop="toggleMoreMenu(member)">
              <span @click.self.capture.stop="toggleMoreMenu(member)">{{$t('calling.More')}}</span>

              <ul :class="[calcMoreMenuClass(member)]">
                <li v-if="member.microphoneOn" @click="closeOtherMicrophone(member)">{{$t('calling.Mute')}}</li>
                <li v-if="member.cameraOn && !member.screenSharingOn" @click="closeOtherCamera(member)">{{$t('calling.CloseVideo')}}</li>
                <li v-if="!member.isGlassDevice" @click="setHost(member)">{{$t('calling.DesignatedHost')}}</li>
                <li @click="kickOutMember(member)">{{$t('calling.Remove')}}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="buttons">
        <BasicBtnGroup layout="horizontal"
                       @primeClick="inviteMore"
                       @defClick="handleCopyInvitationLink">
          <template slot="defContent">
            <svg-icon icon-class="copy"/>
            {{$t('home.CopyInvitationLink')}}
          </template>
          <template slot="primaryContent">
            <svg-icon icon-class="invitemore"/>
            {{$t('home.InviteCollaborators')}}
          </template>
        </BasicBtnGroup>
      </div>
    </div>
    <InviteMoreModal v-model="inviteMoreModalVisible" />
    <CopyInvitationLinkMixin />
  </div>
</template>

<script>
import BasicBtnGroup from '@/components/behavior/basic-btn-group/index.vue'
import UserAvatar from '@/components/business/avatar/UserAvatar.vue'
import Global from '@/global'
import { mapState, mapGetters } from 'vuex'
import InviteMoreModal from '@/components/business/invite-more-modal'
import { kickOutMember, addRoles, deleteRoles, transferRole } from '@/api/talking'
import { ESig } from '@/pb/signaling_param_pb'
import CopyInvitationLinkMixin from '@/components/business/copy-invitation-link-mixin/index.vue'

export default {
  name: 'SessionMember',
  mixins: [CopyInvitationLinkMixin],
  components: { UserAvatar, BasicBtnGroup, InviteMoreModal, CopyInvitationLinkMixin },
  props: {
    allAvatar: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      inviteMoreModalVisible: false,
      statusNameMap: Global.MEMBER_STATUS_NAME,
      hoveredMemberId: undefined,
      moreMenuId: undefined
    }
  },
  computed: {
    ...mapState(['currentSession']),
    ...mapGetters(['sortedMembers', 'localUser', 'realtimeMarkOnUser'])
  },
  async created(){
    this.handleSetInvitationLink(this.currentSession.srcId)
  },
  methods: {
    inviteMore() {
      this.inviteMoreModalVisible = true
    },

    // 悬浮选中成员
    hoverMember (member) {
      if (!this.localUser.isHost) return
      this.hoveredMemberId = member.id
    },

    // 是否展示房间内成员的控制菜单
    isShowInRoomOperation (member) {
      return this.localUser.isHost &&
        member.inRoom &&
        ((this.hoveredMemberId !== this.localUser.id && this.hoveredMemberId === member.id) ||
          this.moreMenuId === member.id)
    },

    // 是否展示不在房间内的成员的控制菜单
    isShowNotInRoomOperation (member) {
      return this.localUser.isHost &&
        this.hoveredMemberId !== this.localUser.id &&
        this.hoveredMemberId === member.id &&
        !member.inRoom
    },

    // 踢人
    kickOutMember (member) {
      kickOutMember({
        conferenceId: this.$store.state.currentSession.srcId,
        kickoutIds: [
          member.id
        ]
      }).then(res => {
        if (+res.data.code === 0) {
          this.$message.success(`${this.$t('calling.RemovedMember').replace('XX', member.name)}`)
        } else {
          this.$message.warn(this.$t('calling.RemovalFailedTryAgain'))
        }
      })
    },

    // 呼叫
    callMember (member) {
      // 如果用户已处在被呼叫中，则屏蔽此次呼叫
      if (member.isWaiting) return
      const { roomId, roomKey, srcId } = this.$store.state.currentSession
      this.$store.dispatch('inviteMemberJoinRoom', {
        roomId,
        roomKey,
        conferenceId: srcId,
        members: [
          {
            userId: member.id,
            nickName: member.name,
            corpId: member.appId
          }
        ]
      })
    },

    // 授予/取消标注权限
    toggleMarkRole (member) {
      const params = {
        conferenceId: this.$store.state.currentSession.srcId,
        members: [{
          userId: member.id,
          roles: [Global.MEMBER_ROLE.ROLE_SPECIALIST]
        }]
      }
      if (member.isAnnotator) {
        deleteRoles(params).then(res => {
          if (+res.data.code === 0) {
            this.$message.success(this.$t('calling.CancelAnnotationPermissionSucc'))
          } else {
            this.$message.warn(this.$t('calling.CancelAnnotationPermissionFailed'))
          }
        })
      } else {
        addRoles(params).then(res => {
          if (+res.data.code === 0) {
            this.$message.success(this.$t('calling.GrantingAnnotationPermissionSucc'))
          } else {
            this.$message.warn(this.$t('calling.GrantAnnotationPermissionFailed'))
          }
        })
      }
    },

    // 设置主持人
    setHost (member) {
      const params = {
        conferenceId: this.$store.state.currentSession.srcId,
        fromId: this.$store.state.userInfo.id,
        toId: member.id,
        role: Global.MEMBER_ROLE.ROLE_HOST
      }
      transferRole(params).then(res => {
        if (+res.data.code === 0) {
          this.closeMoreMenu()
          this.hoverMember({})
          this.$message.success(`${this.$t('calling.designateHostOf').replace('XX', member.name)}`)
        } else {
          this.$message.warn(this.$t('calling.SpecifyHostFailed'))
        }
      })
    },

    // 关闭他人麦克风
    closeOtherMicrophone (member) {
      this.$store.dispatch('controlOtherMemberMedia', {
        userId: member.id,
        option: ESig.ControlOption.MICROPHONE,
        on: Global.DeviceStatus.off
      })
    },

    // 关闭他人摄像头
    closeOtherCamera (member) {
      if (this.checkOtherVideoOccupied(member)) return
      this.$store.dispatch('controlOtherMemberMedia', {
        userId: member.id,
        option: ESig.ControlOption.CAMERA,
        on: Global.DeviceStatus.off
      })
    },

    // 检测用户视频源是否被激光笔功能（当前用户开启或其他用户使用此视频源开启）占用
    checkOtherVideoOccupied (member) {
      const msgKey = 'check-other-video-occupied'
      if (this.realtimeMarkOnUser && +member.id === +this.realtimeMarkOnUser.realtimeMarkTargetUserId) {
        this.$message.warn({
          content: this.$t('calling.TurnoffLaserPointerTryAgain'),
          key: msgKey
        })
        return true
      }
    },

    // 切换更多菜单
    toggleMoreMenu (member) {
      if (this.moreMenuId) {
        this.closeMoreMenu()
      } else {
        this.openMoreMenu(member)
      }
    },

    // 开启更多菜单
    openMoreMenu (member) {
      this.moreMenuId = member.id
      document.addEventListener('click', this.closeMoreMenu)
    },

    // 关闭更多菜单
    closeMoreMenu () {
      this.moreMenuId = undefined
      document.removeEventListener('click', this.closeMoreMenu)
    },

    // 根据菜单内的选项数决定展开高度
    calcMoreMenuClass (member) {
      let count = 1
      member.cameraOn && !member.screenSharingOn && count++
      member.microphoneOn && count++
      !member.isGlassDevice && count++
      return `have-${count}-children`
    },
  }
}
</script>

<style scoped lang="scss">
.root {
  display: flex;
  align-items: center;
  height: 100%;

  .handle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 64px;
    border-radius: 10px 0 0 10px;
    background-color: rgba(#0A0F1A, 20%);
    cursor: pointer;

    .anticon {
      color: white;
    }
    &:hover {
      background-color: rgba(#0A0F1A, 40%);
    }
  }

  .content {
    background-color: white;
    height: 100%;
    display: flex;
    flex-direction: column;

    .header {
      flex: none;
      font-size: 18px;
      font-weight: 600;
      word-break: keep-all;
      height: 56px;
      // width: 383px;
      width: 400px;
      text-align: center;
      line-height: 56px;
    }

    .members {
      flex-grow: 1;
      overflow-y: auto;

      .member {
        margin-top: 20px;
        padding: 4px 24px;
        display: flex;
        align-items: center;
        position: relative;
        cursor: default;

        &.hovered {
          background-color: #F3F6FB;
        }

        .avatar-wrapper {
          margin-right: 16px;
          position: relative;

          .user-avatar {
            font-size: 40px;
            border-radius: 8px;
          }

          > svg {
            position: absolute;
            bottom: 0;
            right: -4px;
            font-size: 16px;
          }

          .mask {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            justify-content: center;
            align-items: center;
            position: absolute;
            top: 0;
            left: 0;
            background-color: #999B9FCC;

            > span {
              font-size: 12px;
              color: #fff;
              line-height: 0;
              transform: scale(0.9);
            }
          }
        }

        .texts {
          flex-grow: 1;
          width: 200px;
          padding-right: 10px;

          .name-role {
            display: flex;
            align-items: center;

            .name {
              font-size: 16px;
              font-weight: bold;
              margin-right: 8px;
            }

            .role {
              font-size: 12px;
              background-color: #EBF1FF;
              border-radius: 4px;
              color: #1f5edb;
              padding: 2px 8px;
            }
          }

          .corp-name {
            color: #999B9F;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .media-status {
          flex-grow: 0;
          display: flex;
          font-size: 20px;
          color: #236cff;

          span {
            width: 20px;
            margin-right: 20px;

            &:last-child {
              margin-right: 0;
            }
          }
        }

        .not-in-room-operation {
          width: 160px;
          height: 100%;
          padding-right: 8px;
          background-image: linear-gradient(to left, rgba(243, 246, 251, 1) 80%, rgba(243, 246, 251, 0));
          position: absolute;
          top: 0;
          right: 0;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          user-select: none;

          > button {
            width: 57px;
            height: 24px;
            background-color: #fff;
            border: none;
            border-radius: 12px;
            font-size: 12px;
            cursor: pointer;

            &.call {
              margin-right: 4px;
              background-color: #236CFF;
              color: #fff;

              &:hover {
                background-color: #578FFF;
              }
            }

            &.kick {
              color: #236CFF;
            }
          }
        }

        .in-room-operation {
          width: 180px;
          height: 100%;
          padding-right: 8px;
          background-image: linear-gradient(to left, rgba(243, 246, 251, 1) 87%, rgba(243, 246, 251, 0));
          position: absolute;
          top: 0;
          right: 0;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          user-select: none;

          > button, > div {
            height: 24px;
            background-color: #236CFF;
            color: #fff;
            border: none;
            border-radius: 12px;
            font-size: 12px;
            cursor: pointer;

            &:hover {
              background-color: #578FFF;
            }
          }

          > button.mark {
            margin-right: 4px;
          }

          > div.more {
            // width: 57px;
            width: 62px;
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;

            > span::after {
              display: inline-block;
              margin-left: 2px;
              content: '▼';
              transition: transform 0.3s;
            }

            > ul {
              visibility: hidden;
              width: 156px;
              height: 0;
              margin: 0;
              padding: 0 8px;
              background-color: #FFF;
              box-shadow: 0 5px 30px 0 rgba(89, 132, 219, 0.08);
              border-radius: 16px;
              border: 1px solid #EDEFF2;
              list-style: none;
              position: absolute;
              top: 24px;
              right: -8px;
              transition: all 0.3s;
              overflow: hidden;

              li {
                height: 38px;
                line-height: 38px;
                padding-left: 16px;
                margin-bottom: 2px;
                font-size: 16px;
                color: #333333;

                &:hover {
                  background-color: #F3F6FB;
                  border-radius: 8px;
                }

                &:first-child {
                  margin-top: 8px;
                }

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }

            &.opened {
              > span::after {
                transform: rotate(180deg);
              }

              > ul {
                visibility: visible;
                z-index: 1;

                &.have-1-children {
                  height: 56px;
                }

                &.have-2-children {
                  height: 96px;
                }

                &.have-3-children {
                  height: 136px;
                }

                &.have-4-children {
                  height: 176px;
                }
              }
            }
          }
        }
      }
    }

    .buttons {
      flex: none;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 97px;
      flex-grow: 0;
      background: rgba(white, 0.85);

      ::v-deep .basic-btn-group > button {
        margin-right: 20px;
        padding: 0 18px;

        .svg-icon {
          margin-right: 8px;
        }

        &:last-child {
          margin-right: 0;
        }
      }
    }

    .header, .buttons {
      border-bottom: 1px solid #edeff2;
      border-top: 1px solid #edeff2;
      box-shadow: 0 -10px 30px 0 rgba(#5984db, 10%);

    }

    .loading-spinner {
      text-align: center;
      line-height: 0;

      > div {
        width: 8px;
        height: 8px;
        line-height: 8px;
        margin-right: 2px;
        background-color: #fff;
        border-radius: 100%;
        display: inline-block;
        animation: bounce-delay 1.4s infinite ease-in-out;
        animation-fill-mode: both;
      }

      .bounce1 {
        animation-delay: -0.32s;
      }

      .bounce2 {
        animation-delay: -0.16s;
      }

      .bounce3 {
        margin-right: 0;
      }
    }
  }
  // TODO:后面版本删除掉
  ::v-deep .ant-btn {
    font-size: 16px;
    font-weight: 500;
    height: 40px;
    border-radius: 20px;
    padding: 0 40px;
    border-color: transparent;
  }
  .primary-btn {
    box-shadow: 0 5px 10px 0 rgba(35, 108, 255, 0.5);
    background: linear-gradient(
        154deg,
        #236cff 0%,
        rgba(35, 108, 255, 0.8) 100%
    );
    color: #fff;
    &:hover {
      background-image: linear-gradient(154deg, #578fff 0%, #578fff 100%);
      box-shadow: 0 0 4px 4px rgba(35, 108, 255, 0.3);
    }
    &:active {
      background: #2C66DB;
      box-shadow: 0 5px 10px 0 rgba(35, 108, 255, 0.5);
    }
  }

  @keyframes bounce-delay {
    0%, 80%, 100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
}
</style>
